/**
 * ApprovalReadinessPanel — drives the Submit-for-Review / Approve
 * buttons. P1's editor already pulls readiness from
 * `GET /api/careplans/{id}/readiness`; this panel surfaces the
 * approval decision and shows the HIGH-risk block reason when
 * applicable. Backend enforces the block on the approve endpoint
 * (HTTP 409 with code HIGH_RISK_BEHAVIOUR_INCOMPLETE).
 */
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertTriangle, ShieldAlert } from 'lucide-react';

export function ApprovalReadinessPanel({ readiness, planStatus, canApprove, onSubmitForReview, onApprove }) {
  const highRisk = readiness?.high_risk_behaviour_findings || [];
  const missing = readiness?.missing_required_fields || [];
  const blocked = highRisk.length > 0;
  const incomplete = missing.length > 0;
  const completion = readiness?.completion_pct ?? 0;

  return (
    <Card data-testid="structured-approval-readiness-panel">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h4 className="font-semibold text-sm">Approval Readiness</h4>
            <p className="text-xs text-muted-foreground">
              Completion <strong>{completion}%</strong> · plan status <strong>{planStatus}</strong>
            </p>
          </div>
          {blocked ? (
            <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">
              <ShieldAlert className="h-3 w-3 mr-1" /> Approval blocked
            </Badge>
          ) : incomplete ? (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              <AlertTriangle className="h-3 w-3 mr-1" /> Missing required fields
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Ready for approval
            </Badge>
          )}
        </div>

        {blocked && (
          <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2"
               data-testid="approval-block-reason">
            HIGH/CRITICAL behaviour rows are missing a linked restrictive practice AND an
            escalation instruction. Resolve every finding then resubmit for approval.
          </div>
        )}

        <div className="flex gap-2 justify-end">
          {planStatus === 'DRAFT' && onSubmitForReview && (
            <Button size="sm" variant="outline"
                    onClick={onSubmitForReview}
                    disabled={blocked}
                    data-testid="approval-submit-review">
              Submit for Review
            </Button>
          )}
          {planStatus === 'REVIEW' && canApprove && onApprove && (
            <Button size="sm"
                    onClick={onApprove}
                    disabled={blocked}
                    data-testid="approval-approve">
              Approve
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default ApprovalReadinessPanel;
